import jsCookie from 'js-cookie';
import { getCountryCallingCode } from 'libphonenumber-js';

/**
 * usePhoneLinkParser
 * Returns a function that parses a text string and returns an array of parts
 * where phone-number substrings (as validated by usePhoneNumberValidation)
 * are identified and returned with tel: hrefs. The original formatting and
 * punctuation are preserved in the content for each part.
 *
 * Each returned part is of the shape:
 * - { type: 'text', content: string }
 * - { type: 'phone', content: string, href: string }
 */
export function usePhoneLinkParser(options = {}) {
  const { strategy = 'window' } = options; // 'window' | 'token'

  const parseMessageWithPhoneLinks = (text) => {
    if (!text || typeof text !== 'string') {
      return [];
    }

    const uPref = jsCookie.get('uPref');
    const uPrefObj = uPref ? JSON.parse(uPref) : {};
    const countryFromUPref = uPrefObj?.shipTo || 'US';
    const callingCode = getCountryCallingCode(countryFromUPref);
    if (strategy === 'token') {
      // Token strategy: only linkify numbers contained within a single token
      const tokens = text.split(/(\s+)/);
      const parts = [];
      let buffer = '';

      const flushBuffer = () => {
        if (buffer) {
          parts.push({ type: 'text', content: buffer });
          buffer = '';
        }
      };

      for (let idx = 0; idx < tokens.length; idx += 1) {
        const token = tokens[idx];
        if (!token.trim()) {
          buffer += token;
          continue; // eslint-disable-line no-continue
        }

        const core = token.replace(/[.,!?]+$/, '');
        const digits = core.replace(/\D/g, '');
        const keepPlus = core.replace(/[^\d+]/g, '');
        const hasPlus = core.trim().startsWith('+');
        const isPhoneLike = (hasPlus && digits.length > 0) || digits.length >= 10;

        if (!isPhoneLike) {
          buffer += token;
          continue; // eslint-disable-line no-continue
        }

        flushBuffer();

        let e164;
        if (keepPlus.startsWith('+')) {
          e164 = keepPlus;
        } else if (digits.startsWith(String(callingCode))) {
          e164 = `+${digits}`;
        } else {
          e164 = `+${callingCode}${digits}`;
        }
        parts.push({ type: 'phone', content: token, href: `tel:${e164}` });
      }

      flushBuffer();
      return parts.length > 0 ? parts : [{ type: 'text', content: text }];
    }

    // Default: window strategy to detect across tokens without merging multiple numbers
    const segments = text.split(/(\s+)/);
    const positions = [];
    let absolute = 0;
    for (let s = 0; s < segments.length; s += 1) {
      positions.push(absolute);
      absolute += segments[s].length;
    }

    const parts = [];
    let cursor = 0;
    let i = 0;
    while (i < segments.length) {
      if (!segments[i].trim()) {
        i += 1;
        continue; // eslint-disable-line no-continue
      }

      let bestEndIndex = -1;
      let bestHref = null;
      let bestStartAbs = -1;
      let bestEndAbs = -1;

      for (let j = i; j < segments.length; j += 1) {
        const windowStart = positions[i];
        const windowEnd = positions[j] + segments[j].length;
        const windowText = text.slice(windowStart, windowEnd);
        const firstRel = windowText.search(/[+\d(]/);
        if (firstRel === -1) {
          continue; // eslint-disable-line no-continue
        }
        let lastRel = -1;
        for (let k = windowText.length - 1; k >= 0; k -= 1) {
          if (/[\d)]/.test(windowText[k])) {
            lastRel = k + 1;
            break;
          }
        }
        if (lastRel === -1 || lastRel <= firstRel) {
          continue; // eslint-disable-line no-continue
        }
        const inner = windowText.slice(firstRel, lastRel).trim();
        if (/\p{L}/u.test(inner)) {
          continue; // eslint-disable-line no-continue
        }
        const rawDigits = inner.replace(/\D/g, '');
        const rawKeepPlus = inner.replace(/[^\d+]/g, '');
        const isPlausible = rawDigits.length >= 10 || (rawKeepPlus.startsWith('+') && rawDigits.length > 0);
        if (!isPlausible) {
          continue; // eslint-disable-line no-continue
        }

        let e164;
        if (rawKeepPlus.startsWith('+')) {
          e164 = rawKeepPlus;
        } else if (rawDigits.startsWith(String(callingCode))) {
          e164 = `+${rawDigits}`;
        } else {
          e164 = `+${callingCode}${rawDigits}`;
        }

        bestEndIndex = j;
        bestHref = `tel:${e164}`;
        bestStartAbs = windowStart + firstRel;
        bestEndAbs = windowStart + lastRel;
      }

      if (bestEndIndex !== -1) {
        const start = bestStartAbs;
        let end = bestEndAbs;
        while (end < text.length && /[.,!?]/.test(text[end])) {
          end += 1;
        }
        if (cursor < start) {
          parts.push({ type: 'text', content: text.slice(cursor, start) });
        }
        parts.push({ type: 'phone', content: text.slice(start, end), href: bestHref });
        cursor = end;
        i = bestEndIndex + 1;
        continue; // eslint-disable-line no-continue
      }

      i += 1;
    }

    if (cursor < text.length) {
      parts.push({ type: 'text', content: text.slice(cursor) });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  return parseMessageWithPhoneLinks;
}
